// src/pages/Login.jsx
import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login, loginWithGoogle, handleGoogleCallback } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams(); // 🔥 ADD THIS

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // 🔥 Check for expired session message
  useEffect(() => {
    const expired = searchParams.get('expired');
    if (expired === 'true') {
      setError('Your session has expired. Please login again.');
    }
  }, [searchParams]);

  // ✅ Redirect if already logged in with VALID token
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (token && user) {
      // 🔥 Check if token is expired before redirecting
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        
        if (payload.exp > now) {
          // Token is valid - redirect
          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        } else {
          // Token expired - clear and stay on login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setError('Your session has expired. Please login again.');
        }
      } catch (e) {
        // Invalid token - clear
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
    }
  }, [navigate, location]);

  // Handle Google OAuth callback
  useEffect(() => {
    const handleGoogleRedirect = async () => {
      const hash = window.location.hash;
      const search = window.location.search;

      if (hash.includes('access_token') || search.includes('code')) {
        setGoogleLoading(true);
        try {
          const result = await handleGoogleCallback();
          console.log('Google login successful:', result);

          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        } catch (err) {
          console.error('Google callback error:', err);
          setError('Google login failed. Please try again.');
        } finally {
          setGoogleLoading(false);
        }
      }
    };

    handleGoogleRedirect();
  }, [handleGoogleCallback, navigate, location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      return setError('Email and password are required.');
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      
      // 🔥 Ensure refresh token is saved
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      if (result.token) {
        localStorage.setItem('token', result.token);
      }
      if (result.user) {
        localStorage.setItem('user', JSON.stringify(result.user));
      }

      window.dispatchEvent(new Event('authChange'));

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      setError(err.message || 'Google login failed.');
      setGoogleLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.logo}>🚀 Marshall AI</h1>
          <h2 style={styles.title}>Welcome Back</h2>
          <p style={styles.subtitle}>Login to access your analyses</p>
        </div>

        {/* 🔥 Error with different styles for expired vs normal */}
        {error && (
          <div style={{
            ...styles.errorBox,
            background: error.includes('expired') ? '#fff8e1' : '#fff0f0',
            border: error.includes('expired') ? '1px solid #ffe082' : '1px solid #ffcdd2',
            color: error.includes('expired') ? '#e65100' : '#c62828',
          }}>
            {error.includes('expired') ? '⏰ ' : '❌ '}
            {error}
          </div>
        )}

        {(loading || googleLoading) ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>
              {googleLoading ? 'Completing Google login...' : 'Logging in...'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleLogin} style={styles.form}>
            <input
              style={styles.input}
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              required
              autoComplete="email"
            />
            <input
              style={styles.input}
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              required
              autoComplete="current-password"
            />
            <button style={styles.btn} type="submit" disabled={loading}>
              Login
            </button>

            <div style={styles.divider}>
              <span style={styles.dividerText}>or</span>
            </div>

            <button
              type="button"
              style={styles.googleBtn}
              onClick={handleGoogleLogin}
              disabled={googleLoading}
            >
              <GoogleIcon /> Continue with Google
            </button>
          </form>
        )}

        <p style={styles.signupLink}>
          Don't have an account?{' '}
          <Link to="/signup" style={{ color: '#764ba2', textDecoration: 'none', fontWeight: '600' }}>
            Sign up
          </Link>
        </p>

        <p style={styles.forgotLink}>
          <Link to="/forgot-password" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>
            Forgot password?
          </Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 8, verticalAlign: 'middle' }}>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2v6h7.9c4.6-4.3 7.3-10.6 7.3-17.2z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.9-6c-2.1 1.4-4.8 2.3-8 2.3-6.1 0-11.3-4.1-13.2-9.7H2.7v6.2C6.7 42.7 14.8 48 24 48z" />
      <path fill="#FBBC05" d="M10.8 28.8c-.5-1.4-.7-2.9-.7-4.8s.3-3.3.7-4.8v-6.2H2.7C1 16.5 0 20.1 0 24s1 7.5 2.7 10.9l8.1-6.1z" />
      <path fill="#EA4335" d="M24 9.5c3.4 0 6.5 1.2 8.9 3.5l6.6-6.6C35.9 2.4 30.4 0 24 0 14.8 0 6.7 5.3 2.7 13.1l8.1 6.2C12.7 13.6 17.9 9.5 24 9.5z" />
    </svg>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)',
  },
  card: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  header: { textAlign: 'center', marginBottom: '28px' },
  logo: { margin: 0, fontSize: '28px', color: '#343183' },
  title: { margin: '12px 0 4px', fontSize: '24px', color: '#333', fontWeight: '700' },
  subtitle: { color: '#666', fontSize: '14px', margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  input: {
    padding: '14px 16px',
    border: '2px solid #e0e7ff',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.3s',
    backgroundColor: '#fafbff',
  },
  btn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #0e4aa5ff, #06b6d4)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  googleBtn: {
    padding: '14px',
    background: 'white',
    color: '#333',
    border: '2px solid #e0e7ff',
    borderRadius: '12px',
    fontSize: '15px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '500',
    transition: 'background 0.2s',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    margin: '8px 0',
  },
  dividerText: {
    flex: 1,
    color: '#aaa',
    fontSize: '13px',
    position: 'relative',
  },
  errorBox: {
    background: '#fff0f0',
    border: '1px solid #ffcdd2',
    color: '#c62828',
    padding: '12px 14px',
    borderRadius: '12px',
    fontSize: '14px',
    marginBottom: '16px',
  },
  signupLink: { textAlign: 'center', marginTop: '20px', color: '#666', fontSize: '14px' },
  forgotLink: { textAlign: 'center', marginTop: '12px' },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 0',
  },
  loadingText: { color: '#666', fontSize: '14px', marginTop: '16px' },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e0e7ff',
    borderTop: '3px solid #06b6d4',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add spinner animation
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}